import { EnemyManager } from '../enemies/EnemyManager';
import { EnemyType } from '../enemies/EnemyType';
import { Enemy } from '../enemies/Enemy';
import { Wave } from './Wave';
import { WaveConfig } from './WaveConfig';
import { store } from '@/store/store';
import { updateWaveInfo } from '@/store/waveSlice';

/**
 * Enum defining different wave types
 */
export enum WaveType {
  NORMAL = 'NORMAL',       // Standard wave with mixed enemies
  BOSS = 'BOSS',           // Wave with a boss enemy
  HORDE = 'HORDE',         // Wave with many weak enemies
  ELITE = 'ELITE',         // Wave with fewer but stronger enemies
  SPECIAL = 'SPECIAL'      // Special event wave
}

/**
 * Interface for wave announcement data
 */
export interface WaveAnnouncement {
  waveNumber: number;
  waveType: WaveType;
  message: string;
  duration: number;
}

/**
 * Manages wave progression and difficulty scaling
 */
export class WaveManager {
  private enemyManager: EnemyManager;
  private currentWave: Wave | null = null;
  private waveNumber: number = 0;
  private enemiesDefeated: number = 0;
  private totalEnemiesDefeated: number = 0;
  private waveActive: boolean = false;
  private waveCompleted: boolean = false;
  private timeBetweenWaves: number = 5.0; // seconds
  private timeSinceLastWave: number = 0;
  private difficultyMultiplier: number = 1.0;
  private waveTypePattern: WaveType[] = [];
  private currentWaveType: WaveType = WaveType.NORMAL;
  private maxWaves: number = 20; // Maximum number of waves before victory
  private lastReportedEnemiesRemaining: number = 0;
  
  // Callbacks
  private onWaveStart: (waveNumber: number) => void = () => {};
  private onWaveComplete: (waveNumber: number) => void = () => {};
  private onAllWavesComplete: () => void = () => {};
  private onWaveAnnouncement: (announcement: WaveAnnouncement) => void = () => {};
  
  /**
   * Creates a new WaveManager
   * @param enemyManager The enemy manager to use
   */
  constructor(enemyManager: EnemyManager) {
    this.enemyManager = enemyManager;
    
    // Set up enemy defeated callback
    this.enemyManager.setOnEnemyDefeated(this.handleEnemyDefeated.bind(this));
    
    // Set up wave type pattern
    this.setupWaveTypePattern();
  }
  
  /**
   * Sets up the pattern of wave types
   */
  private setupWaveTypePattern(): void {
    // Create a pattern of wave types
    // Every 5th wave is a boss wave
    // Every 3rd wave is a horde wave
    // Every 7th wave is an elite wave
    // Others are normal waves
    
    for (let i = 1; i <= this.maxWaves; i++) {
      if (i % 5 === 0) {
        this.waveTypePattern.push(WaveType.BOSS);
      } else if (i % 7 === 0) {
        this.waveTypePattern.push(WaveType.ELITE);
      } else if (i % 3 === 0) {
        this.waveTypePattern.push(WaveType.HORDE);
      } else {
        this.waveTypePattern.push(WaveType.NORMAL);
      }
    }
  }
  
  /**
   * Sets the callback for when a wave starts
   * @param callback The callback function
   */
  public setOnWaveStart(callback: (waveNumber: number) => void): void {
    this.onWaveStart = callback;
  }
  
  /**
   * Sets the callback for when a wave is completed
   * @param callback The callback function
   */
  public setOnWaveComplete(callback: (waveNumber: number) => void): void {
    this.onWaveComplete = callback;
  }
  
  /**
   * Sets the callback for when all waves are completed
   * @param callback The callback function
   */
  public setOnAllWavesComplete(callback: () => void): void {
    this.onAllWavesComplete = callback;
  }
  
  /**
   * Sets the callback for wave announcements
   * @param callback The callback function
   */
  public setOnWaveAnnouncement(callback: (announcement: WaveAnnouncement) => void): void {
    this.onWaveAnnouncement = callback;
  }
  
  /**
   * Starts the wave progression
   */
  public startWaves(): void {
    // Reset wave state
    this.waveNumber = 0;
    this.enemiesDefeated = 0;
    this.totalEnemiesDefeated = 0;
    this.waveActive = false;
    this.waveCompleted = false;
    this.timeSinceLastWave = this.timeBetweenWaves; // Start first wave immediately
    this.difficultyMultiplier = 1.0;
    
    // Clear any existing enemies
    this.enemyManager.clearEnemies();
    
    console.log('Wave progression started');
  }
  
  /**
   * Handles when an enemy is defeated
   */
  private handleEnemyDefeated(): void {
    if (this.waveActive && this.currentWave) {
      this.enemiesDefeated++;
      this.currentWave.updateRemainingEnemiesCount();
      
      // Update the wave info in the Redux store
      this.updateWaveInfo();
      
      // Check if the wave is complete
      if (this.currentWave.isComplete()) {
        this.completeWave();
      }
    }
  }
  
  /**
   * Completes the current wave
   */
  private completeWave(): void {
    if (!this.currentWave) return;
    
    this.waveActive = false;
    
    // Call the wave complete callback
    if (this.onWaveComplete) {
      this.onWaveComplete(this.waveNumber);
    }
    
    // Update the wave info in the Redux store
    this.updateWaveInfo();
    
    // Don't automatically start the next wave
    // The next wave will be started when the player clicks the "Continue" button
  }
  
  /**
   * Starts the next wave
   * This is called when the player clicks the "Continue" button in the upgrade menu
   */
  public startNextWave(): void {
    if (this.waveActive) return;
    
    this.waveNumber++;
    this.startWave(this.waveNumber);
  }
  
  /**
   * Gets the wave type for a given wave number
   * @param waveNumber The wave number
   * @returns The wave type
   */
  private getWaveType(waveNumber: number): WaveType {
    // Use the pattern if available
    if (waveNumber <= this.waveTypePattern.length) {
      return this.waveTypePattern[waveNumber - 1];
    }
    
    // Fallback logic for waves beyond the pattern
    if (waveNumber % 5 === 0) {
      return WaveType.BOSS;
    } else if (waveNumber % 3 === 0) {
      return WaveType.HORDE;
    } else {
      return WaveType.NORMAL;
    }
  }
  
  /**
   * Creates a wave announcement for the current wave
   * @returns The wave announcement
   */
  private createWaveAnnouncement(): WaveAnnouncement {
    let message = `Wave ${this.waveNumber}`;
    let duration = 3.0;
    
    // Add type-specific message
    switch (this.currentWaveType) {
      case WaveType.BOSS:
        message += ": Boss Approaching!";
        duration = 5.0;
        break;
      case WaveType.HORDE:
        message += ": Horde Incoming!";
        duration = 3.5;
        break;
      case WaveType.ELITE:
        message += ": Elite Forces";
        duration = 4.0;
        break;
      case WaveType.SPECIAL:
        message += ": Special Event";
        duration = 4.5;
        break;
      default:
        message += ": Prepare for Battle";
        break;
    }
    
    return {
      waveNumber: this.waveNumber,
      waveType: this.currentWaveType,
      message,
      duration
    };
  }
  
  /**
   * Creates a wave configuration based on the current wave number and type
   * @returns The wave configuration
   */
  private createWaveConfig(): WaveConfig {
    // Base number of enemies
    let baseGrunts = 5;
    let baseArchers = 3;
    let baseBrutes = 1;
    let bossCount = 0;
    
    // Adjust based on wave type
    switch (this.currentWaveType) {
      case WaveType.BOSS:
        // Boss wave: fewer regular enemies, one boss
        baseGrunts = Math.floor(baseGrunts * 0.6);
        baseArchers = Math.floor(baseArchers * 0.6);
        baseBrutes = Math.floor(baseBrutes * 0.6);
        bossCount = 1;
        break;
        
      case WaveType.HORDE:
        // Horde wave: many more grunts, fewer special enemies
        baseGrunts = Math.floor(baseGrunts * 2.5);
        baseArchers = Math.floor(baseArchers * 0.7);
        baseBrutes = Math.floor(baseBrutes * 0.5);
        break;
        
      case WaveType.ELITE:
        // Elite wave: fewer total enemies, but more brutes
        baseGrunts = Math.floor(baseGrunts * 0.7);
        baseArchers = Math.floor(baseArchers * 0.8);
        baseBrutes = Math.floor(baseBrutes * 2.5);
        break;
        
      case WaveType.SPECIAL:
        // Special wave: balanced mix with unique properties
        baseGrunts = Math.floor(baseGrunts * 1.2);
        baseArchers = Math.floor(baseArchers * 1.2);
        baseBrutes = Math.floor(baseBrutes * 1.2);
        break;
    }
    
    // Scale with wave number (apply progressive difficulty)
    const waveScaling = 1 + (this.waveNumber - 1) * 0.15;
    const gruntCount = Math.max(1, Math.floor(baseGrunts + (this.waveNumber - 1) * 2 * waveScaling));
    const archerCount = Math.max(0, Math.floor(baseArchers + (this.waveNumber - 1) * 1.5 * waveScaling));
    const bruteCount = Math.max(0, Math.floor(baseBrutes + (this.waveNumber - 1) * 0.5 * waveScaling));
    
    // Calculate total enemies
    const totalEnemies = gruntCount + archerCount + bruteCount + bossCount;
    
    // Calculate spawn rates (enemies per second)
    let baseSpawnRate = 0.5; // One enemy every 2 seconds
    
    // Adjust spawn rate based on wave type
    switch (this.currentWaveType) {
      case WaveType.HORDE:
        baseSpawnRate *= 1.5; // Faster spawning for hordes
        break;
      case WaveType.ELITE:
        baseSpawnRate *= 0.7; // Slower spawning for elites
        break;
      case WaveType.BOSS:
        baseSpawnRate *= 0.8; // Slower spawning for boss waves
        break;
    }
    
    // Scale spawn rate with wave number
    const spawnRate = Math.min(baseSpawnRate * (1 + (this.waveNumber - 1) * 0.1), 2.5);
    
    // Create wave configuration
    return {
      waveNumber: this.waveNumber,
      enemyCounts: {
        [EnemyType.GRUNT]: gruntCount,
        [EnemyType.ARCHER]: archerCount,
        [EnemyType.BRUTE]: bruteCount,
        [EnemyType.BOSS]: bossCount
      },
      totalEnemies,
      spawnRate,
      difficultyMultiplier: this.difficultyMultiplier,
      waveType: this.currentWaveType
    };
  }
  
  /**
   * Updates the wave manager
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    // Update current wave
    if (this.waveActive && this.currentWave) {
      this.currentWave.update(deltaTime);
      
      // Periodically update the enemies remaining count in the Redux store
      this.updateEnemiesRemainingCount();
    }
    
    // Check if we should start the next wave
    if (!this.waveActive && !this.waveCompleted) {
      this.timeSinceLastWave += deltaTime;
      
      if (this.timeSinceLastWave >= this.timeBetweenWaves) {
        this.startNextWave();
      }
    } else if (this.waveCompleted) {
      this.timeSinceLastWave += deltaTime;
      
      if (this.timeSinceLastWave >= this.timeBetweenWaves) {
        this.waveCompleted = false;
      }
    }
  }
  
  /**
   * Updates the enemies remaining count in the Redux store
   */
  private updateEnemiesRemainingCount(): void {
    if (!this.currentWave) return;
    
    const remainingEnemies = this.currentWave.getRemainingEnemies();
    
    // Only dispatch if the count has changed
    if (remainingEnemies !== this.lastReportedEnemiesRemaining) {
      this.lastReportedEnemiesRemaining = remainingEnemies;
      
      store.dispatch(updateWaveInfo({
        currentWave: this.waveNumber,
        enemiesRemaining: remainingEnemies,
        enemiesDefeated: this.enemiesDefeated,
        waveActive: true
      }));
    }
  }
  
  /**
   * Gets the current wave number
   * @returns The current wave number
   */
  public getCurrentWaveNumber(): number {
    return this.waveNumber;
  }
  
  /**
   * Gets the current wave type
   * @returns The current wave type
   */
  public getCurrentWaveType(): WaveType {
    return this.currentWaveType;
  }
  
  /**
   * Gets the number of enemies defeated in the current wave
   * @returns The number of enemies defeated
   */
  public getEnemiesDefeated(): number {
    return this.enemiesDefeated;
  }
  
  /**
   * Gets the total number of enemies defeated across all waves
   * @returns The total number of enemies defeated
   */
  public getTotalEnemiesDefeated(): number {
    return this.totalEnemiesDefeated;
  }
  
  /**
   * Gets the time until the next wave starts
   * @returns The time until the next wave starts
   */
  public getTimeUntilNextWave(): number {
    if (this.waveActive) return 0;
    
    return Math.max(0, this.timeBetweenWaves - this.timeSinceLastWave);
  }
  
  /**
   * Checks if a wave is active
   * @returns True if a wave is active
   */
  public isWaveActive(): boolean {
    return this.waveActive;
  }
  
  /**
   * Sets the maximum number of waves
   * @param maxWaves The maximum number of waves
   */
  public setMaxWaves(maxWaves: number): void {
    this.maxWaves = maxWaves;
    // Regenerate wave type pattern
    this.waveTypePattern = [];
    this.setupWaveTypePattern();
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    if (this.currentWave) {
      this.currentWave.dispose();
      this.currentWave = null;
    }
  }
  
  /**
   * Updates the wave information in the Redux store
   */
  private updateWaveInfo(): void {
    if (!this.currentWave) return;
    
    const remainingEnemies = this.currentWave.getRemainingEnemies();
    
    store.dispatch(updateWaveInfo({
      currentWave: this.waveNumber,
      enemiesRemaining: remainingEnemies,
      enemiesDefeated: this.enemiesDefeated,
      waveActive: this.waveActive
    }));
  }
  
  /**
   * Starts a specific wave
   * @param waveNumber The wave number to start
   */
  public startWave(waveNumber: number): void {
    // Reset wave state
    this.enemiesDefeated = 0;
    this.waveActive = true;
    
    // Set the current wave number in the EnemyManager
    this.enemyManager.setCurrentWave(waveNumber);
    
    // Determine wave type
    this.currentWaveType = this.getWaveType(waveNumber);
    
    // Create wave configuration
    const waveConfig = this.createWaveConfig();
    
    // Create wave
    this.currentWave = new Wave(waveConfig, this.enemyManager);
    
    // Start wave
    this.currentWave.start();
    
    // Call wave start callback
    if (this.onWaveStart) {
      this.onWaveStart(waveNumber);
    }
    
    // Create and send wave announcement
    const announcement = this.createWaveAnnouncement();
    if (this.onWaveAnnouncement) {
      this.onWaveAnnouncement(announcement);
    }
    
    // Update Redux store
    this.updateWaveInfo();
    
    console.log(`Wave ${waveNumber} (${this.currentWaveType}) started with ${waveConfig.totalEnemies} enemies`);
  }
} 